import type { SiteSettings } from "@/lib/site-defaults";

// Cam sites whose live rooms the streams page lists, one tab each. `source` is the value the streams
// API stores and imports with (streams.source). A platform shows up as soon as it is listed here;
// its rooms arrive once the import accepts its source (lib/import/streams.ts).

export interface StreamPlatform {
  /** Used in the page URL: /cams/stripchat */
  id: string;
  name: string;
  source: string;
  /** Square mark in /public/platforms (64 px WebP: it shows at 30 px at most). */
  logo: string;
  /** Brand colour, for the active tab. */
  color: string;
}

export const STREAM_PLATFORMS = [
  { id: "chaturbate", name: "Chaturbate", source: "chaturbate.com", logo: "/platforms/chaturbate.webp", color: "#f47321" },
  { id: "stripchat", name: "Stripchat", source: "stripchat.com", logo: "/platforms/stripchat.webp", color: "#a33a3a" },
  { id: "livejasmin", name: "LiveJasmin", source: "livejasmin.com", logo: "/platforms/livejasmin.svg", color: "#b00000" },
  { id: "bongacams", name: "BongaCams", source: "ru.bongacams.media", logo: "/platforms/bongacams.webp", color: "#b02040" },
  { id: "cam4", name: "CAM4", source: "cam4.com", logo: "/platforms/cam4.webp", color: "#f15a22" },
] as const satisfies readonly StreamPlatform[];

export type PlatformId = (typeof STREAM_PLATFORMS)[number]["id"];

/**
 * Where each site's live snapshots come from, for preconnect hints on the pages that show them (the
 * first snapshot is what a page is judged by for speed). LiveJasmin spreads its over several hosts.
 */
export const SNAPSHOT_ORIGINS: Partial<Record<PlatformId, string>> = {
  chaturbate: "https://thumb.live.mmcdn.com",
  stripchat: "https://img.doppiocdn.com",
  bongacams: "https://i.bgicdn.com",
  cam4: "https://snapshots.cam4pays.com",
};

/** Who is on camera, as the filters and the streams API name it. */
export const STREAM_GENDERS = [
  { id: "female", label: "Women" },
  { id: "male", label: "Men" },
  { id: "couple", label: "Couples" },
  { id: "trans", label: "Trans" },
] as const;
export type StreamGender = (typeof STREAM_GENDERS)[number]["id"];

/** Where the performer is, as the cam sites group it. */
export const STREAM_REGIONS = [
  { id: "north_america", label: "North American" },
  { id: "south_america", label: "South American" },
  { id: "europe", label: "European & Russian" },
  { id: "asia", label: "Asian" },
  { id: "other", label: "Other regions" },
] as const;
export type StreamRegion = (typeof STREAM_REGIONS)[number]["id"];

export const isStreamGender = (v: unknown): v is StreamGender => STREAM_GENDERS.some((g) => g.id === v);
export const isStreamRegion = (v: unknown): v is StreamRegion => STREAM_REGIONS.some((r) => r.id === v);
/** Lower-cased room name; null unless it is a single path-safe token. */
export function normalizeUsername(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const name = value.trim().toLowerCase();
  return name && name.length <= 100 && !/[\s/?#]/.test(name) ? name : null;
}

/** A tag as stored and filtered on: lower case, no leading #. */
export const normalizeTag = (v: unknown): string | null => {
  if (typeof v !== "string") return null;
  const tag = v.trim().replace(/^#+/, "").toLowerCase();
  return tag && tag.length <= 40 && !/[\s/?#&]/.test(tag) ? tag : null;
};

export const platformById = (id: string | null | undefined): StreamPlatform | null => STREAM_PLATFORMS.find((p) => p.id === id) ?? null;

export const platformBySource = (source: string): StreamPlatform | null => STREAM_PLATFORMS.find((p) => p.source === source) ?? null;

/**
 * Where a room's "Watch" link goes: through the platform's affiliate program when we have its codes
 * (Admin → Settings → Affiliate programs), otherwise the room's own page. `room.url` is the stored link,
 * which keeps the case of the room name (BongaCams' links use it as shown); `room.gender` picks the
 * BongaCash campaign of its section.
 */
export function affiliateRoomUrl(
  source: string,
  username: string,
  affiliates: SiteSettings["affiliates"],
  room: { url?: string; gender?: string | null } = {},
): string | null {
  if (source === "chaturbate.com") {
    const { campaign, tour, track } = affiliates.chaturbate;
    if (!campaign || !tour) return null;
    const q = new URLSearchParams({ tour, campaign, track: track || "default", room: username });
    return `https://chaturbate.com/in/?${q}`;
  }
  if (source === "ru.bongacams.media") {
    const bc = affiliates.bongacams;
    const section = { male: bc.menCampaign, trans: bc.transCampaign, couple: bc.couplesCampaign }[room.gender ?? ""];
    const campaign = section || bc.campaign;
    if (!campaign) return null;
    let name = username;
    try {
      const shown = room.url && new URL(room.url).pathname.split("/").find(Boolean);
      if (shown) name = decodeURIComponent(shown);
    } catch {}
    // As in BongaCash's own room links (its API's chat_url_on_home_page): the tracker, then the room.
    return `https://bongacams.com/track?c=${encodeURIComponent(campaign)}&csurl=https://bongacams.com/${encodeURIComponent(name)}`;
  }
  return null;
}
